import { useCallback, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useBootstrapPrefix } from 'react-bootstrap/ThemeProvider';
import { ArrowLeft, BoxArrowUpRight, Check2, Download, FileEarmarkPdf, Plus } from 'react-bootstrap-icons';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';

import { AsyncPage, Avatar, LoadingButton } from '@/components';
import { createUseThemedStyles, useHandleError } from '@/hooks';
import { ToolAttachmentModel, ToolModel, ToolPreviewModel } from '@/lib/models';
import { advisorService, toolsService } from '@/lib/services';

const useStyles = createUseThemedStyles((theme) => ({
	title: { margin: '0 0 20px', overflowWrap: 'anywhere', color: theme.colors.gray900, ...theme.fonts.l },
	thumbnail: { width: '100%', aspectRatio: '16 / 9', objectFit: 'contain', marginBottom: 24 },
	metadata: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexWrap: 'wrap',
		gap: 16,
		padding: '20px 0',
		margin: '28px 0',
		borderTop: `1px solid ${theme.colors.gray300}`,
		borderBottom: `1px solid ${theme.colors.gray300}`,
	},
	preview: {
		marginBottom: 24,
		padding: '12px 16px',
		borderLeft: `3px solid ${theme.colors.warning}`,
		color: theme.colors.gray800,
	},
	section: { marginBottom: 32 },
	sectionTitle: { marginBottom: 20, ...theme.fonts.m, color: theme.colors.gray900 },
	description: { whiteSpace: 'pre-line', overflowWrap: 'anywhere' },
	author: {
		display: 'flex',
		alignItems: 'center',
		gap: 14,
		padding: '16px 0',
		borderBottom: `1px solid ${theme.colors.gray300}`,
	},
	authorIdentity: { flex: 1, minWidth: 0, overflowWrap: 'anywhere' },
	file: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		padding: '16px 0',
		borderBottom: `1px solid ${theme.colors.gray300}`,
	},
	fileName: { flex: 1, minWidth: 0, overflowWrap: 'anywhere' },
}));

export const ToolDetail = () => {
	const classes = useStyles();
	const buttonPrefix = useBootstrapPrefix(undefined, 'btn');
	const handleError = useHandleError();
	const { toolId, toolVersionId } = useParams<{ toolId: string; toolVersionId?: string }>();
	const [tool, setTool] = useState<ToolModel | ToolPreviewModel>();
	const [followingById, setFollowingById] = useState<Record<string, boolean>>({});
	const isPreview = Boolean(toolVersionId);
	const fetchTool = useCallback(async () => {
		if (!toolId) {
			throw new Error('The accelerator identifier is missing.');
		}
		const response = toolVersionId
			? await toolsService.getToolPreview(toolId, toolVersionId).fetch()
			: await toolsService.getTool(toolId).fetch();
		setTool(response.tool);
	}, [toolId, toolVersionId]);

	const follow = async (advisorId: string, active: boolean) => {
		setFollowingById((current) => ({ ...current, [advisorId]: true }));
		try {
			await advisorService.followAuthor({ authorId: advisorId, follow: active }).fetch();
			setTool(
				(current) =>
					current && {
						...current,
						authors: current.authors.map((author) =>
							author.advisorId === advisorId ? { ...author, userIsFollowing: active } : author
						),
					}
			);
		} catch (error) {
			handleError(error);
		} finally {
			setFollowingById((current) => ({ ...current, [advisorId]: false }));
		}
	};

	const renderAttachments = (title: string, attachments: ToolAttachmentModel[]) =>
		attachments.length > 0 && (
			<section className={classes.section}>
				<h2 className={classes.sectionTitle}>{title}</h2>
				<ul className="list-unstyled">
					{attachments.map((attachment) => (
						<li key={attachment.toolAttachmentId} className={classes.file}>
							<FileEarmarkPdf aria-hidden="true" size={24} className="text-primary" />
							<a
								className={classes.fileName}
								href={attachment.viewUrl}
								target="_blank"
								rel="noopener noreferrer"
							>
								{attachment.fileName}
							</a>
							<a
								className="btn btn-link p-2"
								href={attachment.downloadUrl}
								aria-label={`Download ${attachment.fileName}`}
								title="Download PDF"
							>
								<Download aria-hidden="true" size={18} />
							</a>
						</li>
					))}
				</ul>
			</section>
		);

	const attachments = tool?.attachments ?? [];
	return (
		<AsyncPage fetchData={fetchTool}>
			<Helmet>
				<title>Hackett Connect - {tool?.title ?? 'Accelerator'}</title>
			</Helmet>
			<Container className="py-8">
				<Link to="/tools" className="d-inline-flex align-items-center mb-6">
					<ArrowLeft aria-hidden="true" className="mr-2" />
					Accelerator Library
				</Link>
				{isPreview && (
					<div className={classes.preview} role="status">
						Unpublished preview
					</div>
				)}
				{tool && (
					<>
						<Row>
							<Col md={8}>
								<p className="text-muted mb-3">Accelerator</p>
								<h1 className={classes.title}>{tool.title}</h1>
								{tool.authors.length > 0 && (
									<div className="d-flex flex-wrap" style={{ gap: 16 }}>
										{tool.authors.map((author, index) => (
											<span
												key={author.advisorId ?? `${author.name}-${index}`}
												className="d-inline-flex align-items-center"
											>
												<Avatar size={30} url={author.imageUrl} />
												<strong className="ml-2 small">{author.name}</strong>
											</span>
										))}
									</div>
								)}
							</Col>
							{tool.thumbnailUrl && (
								<Col md={4}>
									<img className={classes.thumbnail} src={tool.thumbnailUrl} alt={tool.title} />
								</Col>
							)}
						</Row>
						<div className={classes.metadata}>
							<span className="text-muted">
								Version {tool.versionLabel}
								{'publishedAt' in tool && tool.publishedAt
									? ` | Published ${format(new Date(tool.publishedAt), 'MMMM d, yyyy')}`
									: ''}
							</span>
							<Link
								className={`${buttonPrefix} ${buttonPrefix}-primary d-inline-flex align-items-center`}
								to={
									isPreview
										? `/tools/${toolId}/versions/${toolVersionId}/preview/launch`
										: `/tools/${toolId}/launch`
								}
							>
								Open Accelerator
								<BoxArrowUpRight aria-hidden="true" className="ml-2" size={18} />
							</Link>
						</div>
						<Row>
							<Col lg={8}>
								<section className={classes.section}>
									<h2 className={classes.sectionTitle}>Description</h2>
									{tool.description ? (
										<div
											className={`html-editor ${classes.description}`}
											dangerouslySetInnerHTML={{ __html: tool.description }}
										/>
									) : (
										<p>Description is not available.</p>
									)}
								</section>
								{tool.releaseNotes && (
									<section className={classes.section}>
										<h2 className={classes.sectionTitle}>In this version</h2>
										<p className={classes.description}>{tool.releaseNotes}</p>
									</section>
								)}
								{tool.authors.length > 0 && (
									<section className={classes.section}>
										<h2 className={classes.sectionTitle}>Authors</h2>
										{tool.authors.map((author, index) => (
											<div
												key={author.advisorId ?? `${author.name}-${index}`}
												className={classes.author}
											>
												<Avatar size={48} url={author.imageUrl} />
												<div className={classes.authorIdentity}>
													{author.advisorId ? (
														<Link to={`/advisory-team/${author.advisorId}`}>
															<strong>{author.name}</strong>
														</Link>
													) : (
														<strong>{author.name}</strong>
													)}
													{author.position && (
														<div className="text-muted small">{author.position}</div>
													)}
												</div>
												{author.advisorId && (
													<LoadingButton
														isLoading={Boolean(followingById[author.advisorId])}
														variant={author.userIsFollowing ? 'primary' : 'outline-primary'}
														classNameInner="d-flex align-items-center"
														onClick={() =>
															follow(author.advisorId!, !author.userIsFollowing)
														}
													>
														{author.userIsFollowing ? (
															<Check2 aria-hidden="true" className="mr-2" />
														) : (
															<Plus aria-hidden="true" className="mr-2" />
														)}
														{author.userIsFollowing ? 'Following' : 'Follow'}
													</LoadingButton>
												)}
											</div>
										))}
									</section>
								)}
							</Col>
							<Col lg={4}>
								{renderAttachments(
									'Documents',
									attachments.filter(
										(attachment) => !attachment.toolVersionId || attachment.toolVersionId === tool.toolVersionId
									)
								)}
							</Col>
						</Row>
					</>
				)}
			</Container>
		</AsyncPage>
	);
};
